import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { CLAIM_TRUTH_CLASSES } from '../data/page-config.mjs';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export type PageHeroVariant =
    | 'rail'
    | 'split'
    | 'stacked'
    | 'wide'
    | 'minimal'
    | 'numbered'
    | 'diagonal'
    | 'manifesto';

type DiagramNode = {
    label: string;
    detail?: string;
    accent?: boolean;
};

type CtaLink = {
    label: string;
    to: string;
    variant?: 'primary' | 'secondary';
};

type CardItem = {
    eyebrow?: string;
    title: string;
    body: string;
    list?: string[];
    truthClass?: string;
};

type TruthItem = {
    title: string;
    description: string;
    truthClass: string;
};

function getTruthTone(truthClass: string) {
    if (truthClass === CLAIM_TRUTH_CLASSES.REAL_NOW) return 'real-now';
    if (truthClass === CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW) return 'prototype-now';
    if (truthClass === CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL) {
        return 'estimated-directional';
    }
    return 'roadmap';
}

function SectionHeader({
    eyebrow,
    title,
    intro,
}: {
    eyebrow?: string;
    title: string;
    intro?: string;
}) {
    return (
        <div className="section-intro">
            {eyebrow ? <p className="text-caption" data-reveal-row>{eyebrow}</p> : null}
            <h2 className="text-section-title" data-reveal-display>{title}</h2>
            {intro ? <p className="text-body-large" data-reveal-row>{intro}</p> : null}
        </div>
    );
}

export function PageFrame({ children }: { children: ReactNode }) {
    const root = useRef<HTMLDivElement | null>(null);

    useGSAP(
        () => {
            gsap.utils.toArray<HTMLElement>('[data-reveal-display]').forEach((el) => {
                gsap.from(el, {
                    y: 60,
                    opacity: 0,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: el, start: 'top 85%', once: true },
                });
            });
            gsap.utils.toArray<HTMLElement>('[data-reveal-row]').forEach((el) => {
                gsap.from(el, {
                    y: 30,
                    opacity: 0,
                    duration: 0.9,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: el, start: 'top 90%', once: true },
                });
            });
        },
        { scope: root },
    );

    return (
        <div className="page-frame" ref={root}>
            {children}
        </div>
    );
}

export function VisualSlot({
    type,
    caption,
    proofClass,
    placeholderText,
}: {
    type: 'render' | 'diagram' | 'screenshot' | 'evidence';
    caption: string;
    proofClass: string;
    placeholderText: string;
}) {
    return (
        <div className="visual-slot">
            <div className="visual-slot__placeholder">
                <span className="visual-slot__type">{type.toUpperCase()}</span>
                <span className="visual-slot__proof">{proofClass}</span>
                <p className="visual-slot__text">{placeholderText}</p>
            </div>
            {caption && <p className="visual-slot__caption">{caption}</p>}
        </div>
    );
}

export function ProofBlock({
    title,
    evidence,
    truthClass,
    caption,
}: {
    title: string;
    evidence: ReactNode;
    truthClass: string;
    caption?: string;
}) {
    return (
        <div className="proof-block">
            <div className="proof-block__header">
                <h4 className="proof-block__title">{title}</h4>
                <span className={`proof-pill pill-${getTruthTone(truthClass)}`}>
                    {truthClass}
                </span>
            </div>
            <div className="proof-block__content">{evidence}</div>
            {caption && <p className="proof-block__caption">{caption}</p>}
        </div>
    );
}

export function PageHero({
    sectionId,
    eyebrow,
    title,
    intro,
    primaryCta,
    secondaryCta,
    proofStrip = [],
    children,
    background,
    variant = 'rail',
    railLabel,
    coord,
    numeral,
}: {
    sectionId?: string;
    eyebrow?: string;
    title: string;
    intro: string;
    primaryCta?: CtaLink;
    secondaryCta?: CtaLink;
    proofStrip?: string[];
    children?: ReactNode;
    background?: ReactNode;
    variant?: PageHeroVariant;
    railLabel?: string;
    coord?: string;
    numeral?: string;
}) {
    const ctas = [primaryCta, secondaryCta].filter(Boolean) as CtaLink[];

    return (
        <section id={sectionId} className={`page-hero page-hero--${variant}`}>
            {background ? <div className="page-hero__background">{background}</div> : null}

            {variant === 'rail' && railLabel ? (
                <span className="page-hero__rail" aria-hidden="true">{railLabel}</span>
            ) : null}
            {variant === 'rail' && coord ? (
                <span className="page-hero__coord" aria-hidden="true">{coord}</span>
            ) : null}
            {numeral ? (
                <span className="page-hero__numeral" aria-hidden="true">{numeral}</span>
            ) : null}
            {variant === 'diagonal' ? (
                <span className="page-hero__diagonal" aria-hidden="true">
                    {railLabel || 'Aven / Control Layer'}
                </span>
            ) : null}

            <div className="site-width page-hero__inner">
                {eyebrow ? (
                    <p className="page-hero__eyebrow" data-reveal-row>{eyebrow}</p>
                ) : null}

                <h1 className="page-hero__title" data-reveal-display>{title}</h1>

                {intro ? (
                    <p className="page-hero__intro" data-reveal-row>{intro}</p>
                ) : null}

                {ctas.length > 0 ? (
                    <div className="page-hero__cta" data-reveal-row>
                        {ctas.map((cta) => (
                            <Link
                                key={`${cta.to}-${cta.label}`}
                                to={cta.to}
                                className={
                                    cta.variant === 'secondary'
                                        ? 'aeq-btn aeq-btn--secondary'
                                        : 'aeq-btn aeq-btn--primary'
                                }
                            >
                                {cta.label}
                            </Link>
                        ))}
                    </div>
                ) : null}

                {children}

                {proofStrip.length > 0 ? (
                    <div className="page-hero__ticker" aria-hidden="true">
                        {proofStrip.map((item, i) => (
                            <span key={item}>
                                {item}
                                {i < proofStrip.length - 1 ? (
                                    <span className="page-hero__ticker-dot"> · </span>
                                ) : null}
                            </span>
                        ))}
                    </div>
                ) : null}
            </div>
        </section>
    );
}

export function ContentSection({
    sectionId,
    eyebrow,
    title,
    intro,
    children,
    borderTop = true,
    featured = false,
}: {
    sectionId?: string;
    eyebrow?: string;
    title: string;
    intro?: string;
    children: ReactNode;
    borderTop?: boolean;
    featured?: boolean;
}) {
    const classes = [
        'page-section',
        featured ? 'page-section--featured' : '',
    ].filter(Boolean).join(' ');

    return (
        <section id={sectionId} className={classes}>
            <div className="site-width">
                {borderTop ? <div className="section-border" /> : null}
                <SectionHeader eyebrow={eyebrow} title={title} intro={intro} />
                {children}
            </div>
        </section>
    );
}

export function CardGrid({
    items,
    columns = 3,
    pattern = 'cards',
}: {
    items: CardItem[];
    columns?: 1 | 2 | 3;
    pattern?: 'cards' | 'meta';
}) {
    if (pattern === 'meta') {
        const pad = (n: number) => String(n + 1).padStart(2, '0');
        return (
            <div className="ed-meta">
                {items.map((item, idx) => {
                    const truthTone = item.truthClass ? getTruthTone(item.truthClass) : null;
                    return (
                        <article key={`${item.title}-${idx}`} className="ed-meta__card">
                            <div className="ed-meta__head">
                                <span className="ed-meta__index">{pad(idx)}</span>
                                <span className="ed-meta__tag">
                                    {item.eyebrow || item.truthClass || ''}
                                </span>
                            </div>
                            {truthTone && !item.eyebrow ? (
                                <span className={`truth-pill truth-pill--${truthTone}`}>
                                    {item.truthClass}
                                </span>
                            ) : null}
                            <h3 className="ed-meta__title">{item.title}</h3>
                            <p className="ed-meta__body">{item.body}</p>
                            {item.list && item.list.length > 0 ? (
                                <ul className="ed-meta__list">
                                    {item.list.map((entry, i) => (
                                        <li
                                            key={`${item.title}-${entry}`}
                                            className="ed-meta__list-item"
                                            data-tag={`· ${pad(i)}`}
                                        >
                                            {entry}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </article>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={`card-grid card-grid--${columns}`}>
            {items.map((item) => {
                const truthTone = item.truthClass ? getTruthTone(item.truthClass) : null;

                return (
                    <article key={`${item.title}-${item.eyebrow || ''}`} className="surface-card">
                        {item.eyebrow ? <p className="text-caption">{item.eyebrow}</p> : null}
                        {truthTone ? (
                            <span className={`truth-pill truth-pill--${truthTone}`}>
                                {item.truthClass}
                            </span>
                        ) : null}
                        <h3 className="text-feature-title">{item.title}</h3>
                        <p className="text-body surface-card__body">{item.body}</p>
                        {item.list && item.list.length > 0 ? (
                            <ul className="surface-card__list">
                                {item.list.map((entry) => (
                                    <li key={`${item.title}-${entry}`} className="text-body">
                                        {entry}
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </article>
                );
            })}
        </div>
    );
}

export function TruthStateGrid({
    items,
    intro,
}: {
    items: TruthItem[];
    intro?: string;
}) {
    return (
        <>
            {intro ? <p className="text-body section-intro-copy">{intro}</p> : null}
            <div className="truth-state-grid">
                {items.map((item) => {
                    const truthTone = getTruthTone(item.truthClass);

                    return (
                        <article key={`${item.truthClass}-${item.title}`} className={`truth-card truth-card--${truthTone}`}>
                            <p className="truth-card__label">{item.truthClass}</p>
                            <h3 className="text-feature-title">{item.title}</h3>
                            <p className="text-body truth-card__body">{item.description}</p>
                        </article>
                    );
                })}
            </div>
        </>
    );
}

export function LinkRow({
    links,
}: {
    links: { label: string; to: string }[];
}) {
    return (
        <div className="link-row">
            {links.map((link) => (
                <Link key={`${link.to}-${link.label}`} to={link.to} className="btn-pill btn-secondary">
                    {link.label}
                </Link>
            ))}
        </div>
    );
}

/**
 * HeroVisual — reserved slot for a premium product render in the hero.
 * Until a real render asset exists, this renders a clean labeled placeholder.
 */
export function HeroVisual({
    alt,
    caption,
    assetSrc,
}: {
    alt: string;
    caption?: string;
    assetSrc?: string;
}) {
    if (assetSrc) {
        return (
            <figure className="hero-visual">
                <img src={assetSrc} alt={alt} className="hero-visual__image" />
                {caption ? (
                    <figcaption className="hero-visual__caption text-body">{caption}</figcaption>
                ) : null}
            </figure>
        );
    }

    return (
        <div className="hero-visual hero-visual--placeholder" aria-label={alt}>
            <div className="hero-visual__slot">
                <span className="hero-visual__label text-caption">Product render</span>
                <p className="hero-visual__note text-body">Premium AvenBox render — asset pending</p>
            </div>
        </div>
    );
}

/**
 * DiagramBlock — structured visual for architecture, routing, or continuity.
 * Accepts either real SVG/image content or renders a labeled placeholder.
 * When `placeholder` is true, shows a clean placeholder block instead of a diagram.
 */
export function DiagramBlock({
    title,
    caption,
    placeholder,
    placeholderLabel,
    children,
}: {
    title?: string;
    caption?: string;
    placeholder?: boolean;
    placeholderLabel?: string;
    children?: ReactNode;
}) {
    if (placeholder) {
        return (
            <div className="diagram-block diagram-block--placeholder">
                <div className="diagram-block__slot">
                    <span className="diagram-block__label text-caption">
                        {placeholderLabel || 'Diagram'}
                    </span>
                    <p className="diagram-block__note text-body">Visual asset pending</p>
                </div>
                {title ? <p className="diagram-block__title text-body">{title}</p> : null}
                {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
            </div>
        );
    }

    return (
        <div className="diagram-block">
            {title ? <p className="diagram-block__title text-body">{title}</p> : null}
            <div className="diagram-block__content">{children}</div>
            {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
        </div>
    );
}

/**
 * SystemLayerStack — structured visual for system layers.
 * Shows named layers in a vertical stack, each with an optional detail line.
 */
export function SystemLayerStack({
    layers,
    caption,
}: {
    layers: DiagramNode[];
    caption?: string;
}) {
    return (
        <div className="diagram-block">
            <div className="layer-stack">
                {layers.map((layer, i) => (
                    <div
                        key={`${layer.label}-${i}`}
                        className={`layer-stack__item${layer.accent ? ' layer-stack__item--accent' : ''}`}
                    >
                        <span className="layer-stack__label">{layer.label}</span>
                        {layer.detail ? (
                            <span className="layer-stack__detail">{layer.detail}</span>
                        ) : null}
                    </div>
                ))}
            </div>
            {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
        </div>
    );
}

/**
 * ExecutionPathDiagram — structured visual for three execution modes.
 * Shows three paths from a single control model.
 */
export function ExecutionPathDiagram({
    modes,
    caption,
}: {
    modes: DiagramNode[];
    caption?: string;
}) {
    return (
        <div className="diagram-block">
            <div className="execution-paths">
                <div className="execution-paths__origin">
                    <span className="execution-paths__origin-label">Control model</span>
                </div>
                <div className="execution-paths__routes">
                    {modes.map((mode, i) => (
                        <div
                            key={`${mode.label}-${i}`}
                            className={`execution-paths__mode${mode.accent ? ' execution-paths__mode--accent' : ''}`}
                        >
                            <span className="execution-paths__mode-label">{mode.label}</span>
                            {mode.detail ? (
                                <span className="execution-paths__mode-detail">{mode.detail}</span>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
            {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
        </div>
    );
}

/**
 * TrustBoundaryDiagram — structured visual for trust boundary.
 * Shows what sits inside vs outside the default boundary.
 */
export function TrustBoundaryDiagram({
    inside,
    outside,
    boundaryLabel,
    caption,
}: {
    inside: DiagramNode[];
    outside: DiagramNode[];
    boundaryLabel?: string;
    caption?: string;
}) {
    return (
        <div className="diagram-block">
            <div className="trust-boundary">
                <div className="trust-boundary__zone trust-boundary__zone--inside">
                    <span className="trust-boundary__zone-label">
                        {boundaryLabel || 'Default trust boundary'}
                    </span>
                    <div className="trust-boundary__items">
                        {inside.map((item, i) => (
                            <span
                                key={`in-${item.label}-${i}`}
                                className="trust-boundary__item trust-boundary__item--inside"
                            >
                                {item.label}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="trust-boundary__divider" />
                <div className="trust-boundary__zone trust-boundary__zone--outside">
                    <span className="trust-boundary__zone-label">Explicit extension only</span>
                    <div className="trust-boundary__items">
                        {outside.map((item, i) => (
                            <span
                                key={`out-${item.label}-${i}`}
                                className="trust-boundary__item trust-boundary__item--outside"
                            >
                                {item.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
            {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
        </div>
    );
}

/**
 * RoutingLogicDiagram — structured visual for the three routing paths.
 * Shows how AvenBox governs where context flows: local (default),
 * trusted extension (explicit), and external (bounded, explicit only).
 * Used on the Security page to make routing feel architectural.
 */
export function RoutingLogicDiagram({ caption }: { caption?: string }) {
    return (
        <div className="diagram-block">
            <div className="routing-logic">
                <div className="routing-logic__context">
                    <span className="routing-logic__context-label">Your context / work</span>
                </div>
                <div className="routing-logic__node">
                    <span className="routing-logic__node-label">AvenBox routing decision</span>
                    <span className="routing-logic__node-sub">Local-first by default</span>
                </div>
                <div className="routing-logic__paths">
                    <div className="routing-logic__path routing-logic__path--primary">
                        <span className="routing-logic__path-label">Local</span>
                        <span className="routing-logic__path-sub">Private by default. Near-zero latency. Always available offline.</span>
                        <span className="routing-logic__path-tag">Default path</span>
                    </div>
                    <div className="routing-logic__path">
                        <span className="routing-logic__path-label">Trusted extension</span>
                        <span className="routing-logic__path-sub">User-controlled infrastructure. Capability scales without surrendering the control model.</span>
                        <span className="routing-logic__path-tag">Explicit only</span>
                    </div>
                    <div className="routing-logic__path routing-logic__path--external">
                        <span className="routing-logic__path-label">External service</span>
                        <span className="routing-logic__path-sub">Bounded, visible, never a silent default. Used only when explicitly chosen.</span>
                        <span className="routing-logic__path-tag">Explicit + bounded</span>
                    </div>
                </div>
            </div>
            {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
        </div>
    );
}

/**
 * ContinuityDiagram — structured visual for device + app + workstation relationship.
 */
export function ContinuityDiagram({
    nodes,
    caption,
}: {
    nodes: DiagramNode[];
    caption?: string;
}) {
    return (
        <div className="diagram-block">
            <div className="continuity-diagram">
                <div className="continuity-diagram__hub">
                    <span className="continuity-diagram__hub-label">AvenBox</span>
                </div>
                <div className="continuity-diagram__surfaces">
                    {nodes.map((node, i) => (
                        <div
                            key={`${node.label}-${i}`}
                            className={`continuity-diagram__surface${node.accent ? ' continuity-diagram__surface--accent' : ''}`}
                        >
                            <span className="continuity-diagram__surface-label">{node.label}</span>
                            {node.detail ? (
                                <span className="continuity-diagram__surface-detail">{node.detail}</span>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
            {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
        </div>
    );
}

/**
 * SystemStatusPanel — compact in-hero status panel showing current system state.
 * Honest representation of what is active, prototype, and roadmap.
 * Used in the homepage hero to replace a placeholder render slot.
 * No fake metrics, screenshots, or aspirational claims.
 */
export function SystemStatusPanel() {
    return (
        <div className="system-status-panel">
            <span className="system-status-panel__label">System status</span>
            <div className="system-status-panel__items">
                <div className="system-status-panel__item system-status-panel__item--active">
                    <span className="system-status-panel__dot" />
                    <span className="system-status-panel__name">Local AI backend</span>
                    <span className="system-status-panel__state">Active</span>
                </div>
                <div className="system-status-panel__item system-status-panel__item--active">
                    <span className="system-status-panel__dot" />
                    <span className="system-status-panel__name">Orchestration core</span>
                    <span className="system-status-panel__state">Active</span>
                </div>
                <div className="system-status-panel__item system-status-panel__item--prototype">
                    <span className="system-status-panel__dot" />
                    <span className="system-status-panel__name">PC continuity</span>
                    <span className="system-status-panel__state">Prototype</span>
                </div>
                <div className="system-status-panel__item system-status-panel__item--roadmap">
                    <span className="system-status-panel__dot" />
                    <span className="system-status-panel__name">Production hardware</span>
                    <span className="system-status-panel__state">Q1 2027</span>
                </div>
            </div>
        </div>
    );
}

/**
 * ArchitectureDiagram — primary system architecture visual for /technology.
 * Shows AvenBox as sovereign control layer between user devices and three execution paths.
 * Internal layers (local intelligence, orchestration, trust boundary, continuity) are
 * shown as structural chips inside the control layer node.
 * Design: dark/premium, CSS-only, no heavy animation.
 */
export function ArchitectureDiagram({ caption }: { caption?: string }) {
    return (
        <div className="diagram-block">
            <div className="arch-diagram">
                <div className="arch-diagram__input">
                    <span className="arch-diagram__input-label">User devices &amp; context</span>
                </div>
                <div className="arch-diagram__core">
                    <div className="arch-diagram__core-header">
                        <span className="arch-diagram__core-title">AvenBox</span>
                        <span className="arch-diagram__core-sub">Sovereign control layer</span>
                    </div>
                    <div className="arch-diagram__core-layers">
                        <span className="arch-diagram__core-layer arch-diagram__core-layer--primary">
                            Local intelligence
                        </span>
                        <span className="arch-diagram__core-layer">Orchestration</span>
                        <span className="arch-diagram__core-layer">Trust boundary</span>
                        <span className="arch-diagram__core-layer">Continuity</span>
                    </div>
                </div>
                <div className="arch-diagram__paths">
                    <div className="arch-diagram__path arch-diagram__path--default">
                        <span className="arch-diagram__path-label">Local execution</span>
                        <span className="arch-diagram__path-detail">
                            Privacy anchor. Near-zero latency. Always offline-capable.
                        </span>
                        <span className="arch-diagram__path-tag">Default path</span>
                    </div>
                    <div className="arch-diagram__path">
                        <span className="arch-diagram__path-label">Trusted extension</span>
                        <span className="arch-diagram__path-detail">
                            User-controlled private infrastructure. Capability scales without surrendering authority.
                        </span>
                        <span className="arch-diagram__path-tag">Explicit only</span>
                    </div>
                    <div className="arch-diagram__path">
                        <span className="arch-diagram__path-label">Controlled external</span>
                        <span className="arch-diagram__path-detail">
                            Bounded, visible, never a silent default. Explicit choice only.
                        </span>
                        <span className="arch-diagram__path-tag">Explicit + bounded</span>
                    </div>
                </div>
            </div>
            {caption ? <p className="diagram-block__caption text-body">{caption}</p> : null}
        </div>
    );
}
